import React, { useState, memo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, BookOpenIcon, FingerprintIcon, SlidersIcon, TrendingUpIcon, ShieldCheckIcon, UsersIcon, BarChart3Icon, CheckCircleIcon, HelpCircleIcon, ClockIcon, AlertCircleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export const MethodologyAccordion: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isStimulusExpanded, setIsStimulusExpanded] = useState(false);
  return <div className="bg-[#FFFFFF] rounded-[4px] border border-[#E5E5E5] overflow-hidden print:border print:border-[#E5E5E5] print:break-inside-avoid" data-id="element-405">
      {/* Trigger Button */}
      <button onClick={() => setIsExpanded(!isExpanded)} className="w-full p-6 md:p-8 flex items-center justify-between hover:bg-[#F9FAFB] transition-colors text-left group cursor-pointer" data-id="element-406">
        <div className="flex items-start md:items-center space-x-4" data-id="element-407">
          <div className="p-2 bg-[#C00000]/10 rounded-sm group-hover:bg-[#C00000]/20 transition-colors" data-id="element-408">
            <BookOpenIcon className="h-6 w-6 text-[#C00000]" data-id="element-409" />
          </div>
          <div data-id="element-410">
            <h2 className="text-lg md:text-xl font-bold text-[#171717] group-hover:text-[#C00000] transition-colors" data-id="element-411">
              Methodology: How We Measure Resonance
            </h2>
            <p className="text-sm text-[#AAAAAA] mt-1" data-id="element-412">
              {isExpanded ? 'Click to collapse' : 'Click to expand'} •
              Understanding the science behind your +33% brand lift
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2" data-id="element-413">
          <span className="text-xs font-medium text-[#AAAAAA] hidden sm:block" data-id="element-414">
            {isExpanded ? 'Hide' : 'Show'}
          </span>
          {isExpanded ? <ChevronUpIcon className="h-5 w-5 text-[#C00000] flex-shrink-0 ml-4 group-hover:scale-110 transition-transform" data-id="element-415" /> : <ChevronDownIcon className="h-5 w-5 text-[#C00000] flex-shrink-0 ml-4 group-hover:scale-110 transition-transform" data-id="element-416" />}
        </div>
      </button>

      <AnimatePresence data-id="element-417">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-418">
            <div className="p-6 md:p-8 pt-0 border-t border-[#E5E5E5] bg-[#F9FAFB] space-y-8" data-id="element-419">
              {/* Executive Summary */}
              <div className="bg-[#FFFFFF] p-6 rounded-[4px] border-l-4 border-[#C00000] border-y border-r border-y-[#E5E5E5] border-r-[#E5E5E5]" data-id="element-420">
                <div className="flex items-center space-x-2 mb-3" data-id="element-421">
                  <ClockIcon className="h-4 w-4 text-[#C00000]" data-id="element-422" />
                  <span className="text-xs font-semibold text-[#C00000] uppercase tracking-wider" data-id="element-423">
                    30-Second Summary
                  </span>
                </div>
                <p className="text-base text-[#000000] leading-relaxed font-medium" data-id="element-424">
                  We observed 279,000+ unique individuals from your target
                  audience across three distinct phases: before, during, and
                  after your TV campaign. Using a constant neutral stimulus
                  across all phases, we measured how TV media pressure
                  influenced behavioral engagement. The result: a{' '}
                  <span className="text-[#C00000] font-bold" data-id="element-425">
                    validated +33% increase
                  </span>{' '}
                  in brand interaction during the campaign flight (97.5%
                  confidence), with a promising{' '}
                  <span className="text-[#C00000] font-bold" data-id="element-426">
                    +14% sustained elevation
                  </span>{' '}
                  post-campaign (directional evidence).
                </p>
              </div>

              {/* Three Core Principles */}
              <div data-id="element-427">
                <h3 className="text-lg font-bold text-[#171717] mb-4" data-id="element-428">
                  The Three Core Principles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-429">
                  {/* The Signal */}
                  <div className="bg-[#FFFFFF] p-6 rounded-[4px] border border-[#E5E5E5]" data-id="element-430">
                    <div className="flex items-center space-x-3 mb-4" data-id="element-431">
                      <FingerprintIcon className="h-6 w-6 text-[#C00000]" data-id="element-432" />
                      <h4 className="text-base font-bold text-[#171717]" data-id="element-433">
                        The Signal
                      </h4>
                    </div>
                    <p className="text-sm font-bold text-[#171717] mb-2" data-id="element-434">
                      Actual vs. Claimed Behavior
                    </p>
                    <p className="text-sm text-[#000000] leading-relaxed" data-id="element-435">
                      Traditional research asks people what they remember
                      seeing. Brandpulse tracks what they subconsciously do. We
                      observed 279,000+ unique individuals in their natural
                      viewing environment, capturing authentic behavioral
                      responses rather than claimed recall.
                    </p>
                  </div>

                  {/* The Constant Stimulus */}
                  <div className="bg-[#FFFFFF] p-6 rounded-[4px] border border-[#E5E5E5]" data-id="element-436">
                    <div className="flex items-center space-x-3 mb-4" data-id="element-437">
                      <SlidersIcon className="h-6 w-6 text-[#C00000]" data-id="element-438" />
                      <h4 className="text-base font-bold text-[#171717]" data-id="element-439">
                        The Constant Stimulus
                      </h4>
                    </div>
                    <p className="text-sm font-bold text-[#171717] mb-2" data-id="element-440">
                      Isolating TV Impact
                    </p>
                    <p className="text-sm text-[#000000] leading-relaxed mb-4" data-id="element-441">
                      To isolate the pure impact of your TV campaign, we
                      deployed a neutral, constant brand stimulus across all
                      measurement phases. Think of it as a thermometer: the
                      stimulus measures the temperature, while your TV campaign
                      is the heat source.
                    </p>

                    {/* Expandable Stimulus Image */}
                    <button onClick={() => setIsStimulusExpanded(!isStimulusExpanded)} className="w-full text-left flex items-center justify-between text-sm font-medium text-[#C00000] hover:text-[#C00000]/80 transition-colors" data-id="element-442">
                      <span data-id="element-443">
                        {isStimulusExpanded ? 'Hide' : 'View'} Constant Stimulus
                      </span>
                      {isStimulusExpanded ? <ChevronUpIcon className="h-4 w-4" data-id="element-444" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-445" />}
                    </button>

                    <AnimatePresence data-id="element-446">
                      {isStimulusExpanded && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-447">
                          <div className="mt-4 p-4 bg-[#F9FAFB] rounded-sm border border-[#E5E5E5]" data-id="element-448">
                            <div className="relative w-full aspect-[4/3] rounded-sm overflow-hidden bg-[#FFFFFF] border border-[#E5E5E5]" data-id="element-449">
                              {/* Illustrative badge */}
                              <div className="absolute top-3 left-3 z-10 px-2 py-1 rounded-sm bg-[#FFFFFF] border border-[#E5E5E5] text-[9px] font-semibold uppercase tracking-wider text-[#AAAAAA]" data-id="element-450">
                                Illustrative example
                              </div>

                              {/* Neutral ad layout — name and logo only */}
                              <div className="absolute inset-0 flex items-center justify-center" data-id="element-451">
                                <div className="flex items-center gap-3" data-id="element-452">
                                  <div className="w-12 h-12 rounded-sm bg-[#171717] flex items-center justify-center" data-id="element-453">
                                    <span className="text-[#FFFFFF] font-bold text-xl" data-id="element-454">
                                      X
                                    </span>
                                  </div>
                                  <span className="text-[#171717] font-bold text-2xl tracking-tight" data-id="element-455">
                                    BrandX
                                  </span>
                                </div>
                              </div>
                            </div>
                            <p className="text-xs text-[#AAAAAA] mt-3 text-center italic" data-id="element-456">
                              Illustrative mockup for a fictional brand · Shown
                              consistently across all three measurement phases
                            </p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* The Metric */}
                  <div className="bg-[#FFFFFF] p-6 rounded-[4px] border border-[#E5E5E5]" data-id="element-457">
                    <div className="flex items-center space-x-3 mb-4" data-id="element-458">
                      <TrendingUpIcon className="h-6 w-6 text-[#C00000]" data-id="element-459" />
                      <h4 className="text-base font-bold text-[#171717]" data-id="element-460">
                        The Metric
                      </h4>
                    </div>
                    <p className="text-sm font-bold text-[#171717] mb-2" data-id="element-461">
                      Uplift as Activation
                    </p>
                    <p className="text-sm text-[#000000] leading-relaxed" data-id="element-462">
                      Uplift is not passive awareness, it's active engagement. A
                      +33% uplift means your target audience demonstrated 33%
                      more interaction behavior with your brand compared to the
                      pre-campaign baseline.
                    </p>
                  </div>
                </div>
              </div>

              {/* Three-Phase Measurement Design */}
              <div className="bg-[#FFFFFF] p-6 rounded-[4px] border border-[#E5E5E5]" data-id="element-463">
                <h3 className="text-lg font-bold text-[#171717] mb-4" data-id="element-464">
                  Three-Phase Measurement Design
                </h3>
                <div className="space-y-4" data-id="element-465">
                  <div className="flex items-start space-x-3" data-id="element-466">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F9FAFB] text-[#AAAAAA] flex items-center justify-center text-sm font-bold" data-id="element-467">
                      1
                    </div>
                    <div data-id="element-468">
                      <p className="text-sm font-bold text-[#171717]" data-id="element-469">
                        Baseline (Pre-Campaign)
                      </p>
                      <p className="text-sm text-[#000000]" data-id="element-470">
                        Measured target audience behavior before TV campaign
                        launch. Established natural engagement levels without
                        media pressure. Period: December 9-14
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-471">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#C00000]/10 text-[#C00000] flex items-center justify-center text-sm font-bold" data-id="element-472">
                      2
                    </div>
                    <div data-id="element-473">
                      <p className="text-sm font-bold text-[#171717]" data-id="element-474">
                        Flight (During Campaign)
                      </p>
                      <p className="text-sm text-[#000000]" data-id="element-475">
                        Measured same target audience while TV ads were airing.
                        Captured behavioral response under active media
                        pressure. Period: December 15-31
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-476">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F9FAFB] text-[#AAAAAA] flex items-center justify-center text-sm font-bold" data-id="element-477">
                      3
                    </div>
                    <div data-id="element-478">
                      <p className="text-sm font-bold text-[#171717]" data-id="element-479">
                        Post-Flight (After Campaign)
                      </p>
                      <p className="text-sm text-[#000000]" data-id="element-480">
                        Measured target audience after TV campaign concluded.
                        Assessed sustained impact and brand equity retention.
                        Period: January 1-7
                      </p>
                    </div>
                  </div>
                </div>

                {/* Audience Exclusion Feature */}
                <div className="mt-6 p-4 bg-[#C00000]/5 rounded-sm border border-[#C00000]/20" data-id="element-481">
                  <div className="flex items-start space-x-3" data-id="element-482">
                    <ShieldCheckIcon className="h-5 w-5 text-[#C00000] flex-shrink-0 mt-0.5" data-id="element-483" />
                    <div data-id="element-484">
                      <p className="text-sm font-bold text-[#171717] mb-2" data-id="element-485">
                        Critical Design Feature: Audience Exclusion
                      </p>
                      <p className="text-sm text-[#000000] leading-relaxed" data-id="element-486">
                        To ensure that observed uplift stems from TV media
                        pressure, not from repeated exposure to our measurement
                        stimulus, we exclude individuals from one phase when
                        observing subsequent phases. This prevents recognition
                        bias and guarantees that behavioral changes reflect
                        genuine campaign impact.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Statistical Power Through Scale */}
              <div className="bg-[#FFFFFF] p-6 rounded-[4px] border border-[#E5E5E5]" data-id="element-487">
                <h3 className="text-lg font-bold text-[#171717] mb-4" data-id="element-488">
                  Why Large-Scale Observation Matters
                </h3>
                <p className="text-sm font-bold text-[#171717] mb-3" data-id="element-489">
                  Statistical Power Through Scale
                </p>
                <p className="text-sm text-[#000000] leading-relaxed mb-4" data-id="element-490">
                  Brandpulse operates on a binary response model: we observe
                  whether individuals engage with the brand stimulus or do not.
                  Both outcomes are valuable data points.
                </p>
                <p className="text-sm text-[#000000] leading-relaxed mb-4" data-id="element-491">
                  With a traditional research panel of 1,000 people, subtle
                  behavioral shifts are statistically invisible, the sample is
                  too small to detect meaningful change. By observing 279,000+
                  unique individuals, even modest shifts in engagement become
                  statistically validated and actionable.
                </p>
                <p className="text-sm text-[#000000] leading-relaxed" data-id="element-492">
                  This is the advantage of mass-audience observation: we measure
                  the entire market's response, not a small sample's opinion.
                </p>
              </div>

              {/* Statistical Validation with Confidence Levels */}
              <div className="bg-[#FFFFFF] p-6 rounded-[4px] border border-[#E5E5E5]" data-id="element-493">
                <h3 className="text-lg font-bold text-[#171717] mb-4" data-id="element-494">
                  Statistical Validation & Confidence Levels
                </h3>
                <div className="space-y-4" data-id="element-495">
                  <div data-id="element-496">
                    <p className="text-sm font-bold text-[#171717] mb-2" data-id="element-497">
                      Bayesian Analysis
                    </p>
                    <p className="text-sm text-[#000000] leading-relaxed" data-id="element-498">
                      We employ Bayesian structural time-series modeling to
                      calculate uplift with precision. This approach compares
                      observed behavioral patterns across the three phases,
                      isolating the campaign's causal impact from external
                      market noise.
                    </p>
                  </div>

                  {/* Campaign Period Confidence */}
                  <div className="p-4 bg-[#F9FAFB] rounded-sm border-l-4 border-[#C00000] border-y border-r border-y-[#E5E5E5] border-r-[#E5E5E5]" data-id="element-499">
                    <div className="flex items-start space-x-3" data-id="element-500">
                      <CheckCircleIcon className="h-5 w-5 text-[#AAAAAA] flex-shrink-0 mt-0.5" data-id="element-501" />
                      <div data-id="element-502">
                        <p className="text-sm font-bold text-[#171717] mb-1" data-id="element-503">
                          Campaign Period (+33% Uplift): 97.5% Confidence
                        </p>
                        <p className="text-sm text-[#000000] leading-relaxed" data-id="element-504">
                          We can state with 97.5% certainty that your TV
                          campaign caused the +33% behavioral uplift during the
                          flight period. This level of confidence meets CFO
                          standards for marketing investment justification.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Post-Flight Confidence */}
                  <div className="p-4 bg-[#F9FAFB] rounded-sm border border-[#E5E5E5]" data-id="element-505">
                    <div className="flex items-start space-x-3" data-id="element-506">
                      <AlertCircleIcon className="h-5 w-5 text-[#AAAAAA] flex-shrink-0 mt-0.5" data-id="element-507" />
                      <div data-id="element-508">
                        <p className="text-sm font-bold text-[#171717] mb-1" data-id="element-509">
                          Post-Flight Period (+14% Sustained): Directional
                          Evidence
                        </p>
                        <p className="text-sm text-[#000000] leading-relaxed mb-2" data-id="element-510">
                          The +14% sustained lift observed in the two weeks
                          following campaign conclusion represents strong
                          directional evidence of lasting brand equity. However,
                          this post-flight period is shorter than ideal for
                          statistical validation (larger margin of error).
                        </p>
                        <p className="text-sm text-[#000000] leading-relaxed" data-id="element-511">
                          <span className="font-bold text-[#171717]" data-id="element-512">
                            For future tests:
                          </span>{' '}
                          Extending the post-flight observation window increases
                          statistical power and improves the likelihood of
                          reaching 90-95% confidence on sustained impact.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparison Table */}
              <div className="overflow-hidden rounded-sm border border-[#E5E5E5]" data-id="element-513">
                <table className="min-w-full divide-y divide-[#E5E5E5]" data-id="element-514">
                  <thead className="bg-[#F9FAFB]" data-id="element-515">
                    <tr data-id="element-516">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-[#C00000] uppercase tracking-wider" data-id="element-517">
                        Dimension
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-[#AAAAAA] uppercase tracking-wider" data-id="element-518">
                        Traditional Survey
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-[#C00000] uppercase tracking-wider" data-id="element-519">
                        Brandpulse Resonance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-[#FFFFFF] divide-y divide-[#E5E5E5]" data-id="element-520">
                    <tr data-id="element-521">
                      <td className="px-6 py-4 text-sm font-bold text-[#171717]" data-id="element-522">
                        Sample
                      </td>
                      <td className="px-6 py-4 text-sm text-[#AAAAAA]" data-id="element-523">
                        ~1,000 respondents
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-[#171717]" data-id="element-524">
                        279,000+ observations
                      </td>
                    </tr>
                    <tr className="bg-[#F9FAFB]" data-id="element-525">
                      <td className="px-6 py-4 text-sm font-bold text-[#171717]" data-id="element-526">
                        Method
                      </td>
                      <td className="px-6 py-4 text-sm text-[#AAAAAA]" data-id="element-527">
                        Self-reported recall
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-[#171717]" data-id="element-528">
                        Observed behavior
                      </td>
                    </tr>
                    <tr data-id="element-529">
                      <td className="px-6 py-4 text-sm font-bold text-[#171717]" data-id="element-530">
                        Environment
                      </td>
                      <td className="px-6 py-4 text-sm text-[#AAAAAA]" data-id="element-531">
                        Survey panel
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-[#171717]" data-id="element-532">
                        Natural viewing environment
                      </td>
                    </tr>
                    <tr className="bg-[#F9FAFB]" data-id="element-533">
                      <td className="px-6 py-4 text-sm font-bold text-[#171717]" data-id="element-534">
                        Timing
                      </td>
                      <td className="px-6 py-4 text-sm text-[#AAAAAA]" data-id="element-535">
                        Post-campaign retrospective
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-[#171717]" data-id="element-536">
                        Real-time + sustained tracking
                      </td>
                    </tr>
                    <tr data-id="element-537">
                      <td className="px-6 py-4 text-sm font-bold text-[#171717]" data-id="element-538">
                        Certainty
                      </td>
                      <td className="px-6 py-4 text-sm text-[#AAAAAA]" data-id="element-539">
                        Directional indication
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-[#C00000]" data-id="element-540">
                        97.5% statistical validation
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Quick Reference */}
              <div className="bg-[#C00000]/5 p-6 rounded-sm border border-[#C00000]/20" data-id="element-541">
                <h4 className="text-xs font-semibold text-[#C00000] uppercase tracking-wider mb-4" data-id="element-542">
                  Quick Reference for Executives
                </h4>
                <ul className="space-y-3" data-id="element-543">
                  <li className="flex items-start space-x-2" data-id="element-544">
                    <CheckCircleIcon className="h-4 w-4 text-[#AAAAAA] mt-0.5 flex-shrink-0" data-id="element-545" />
                    <span className="text-sm text-[#000000]" data-id="element-546">
                      <span className="font-bold text-[#171717]" data-id="element-547">
                        Measured:
                      </span>{' '}
                      Real behavior in natural viewing environments
                    </span>
                  </li>
                  <li className="flex items-start space-x-2" data-id="element-548">
                    <CheckCircleIcon className="h-4 w-4 text-[#AAAAAA] mt-0.5 flex-shrink-0" data-id="element-549" />
                    <span className="text-sm text-[#000000]" data-id="element-550">
                      <span className="font-bold text-[#171717]" data-id="element-551">Sample:</span>{' '}
                      279,000+ unique individuals
                    </span>
                  </li>
                  <li className="flex items-start space-x-2" data-id="element-552">
                    <CheckCircleIcon className="h-4 w-4 text-[#AAAAAA] mt-0.5 flex-shrink-0" data-id="element-553" />
                    <span className="text-sm text-[#000000]" data-id="element-554">
                      <span className="font-bold text-[#171717]" data-id="element-555">
                        Campaign Impact:
                      </span>{' '}
                      +33% uplift (97.5% confidence)
                    </span>
                  </li>
                  <li className="flex items-start space-x-2" data-id="element-556">
                    <CheckCircleIcon className="h-4 w-4 text-[#AAAAAA] mt-0.5 flex-shrink-0" data-id="element-557" />
                    <span className="text-sm text-[#000000]" data-id="element-558">
                      <span className="font-bold text-[#171717]" data-id="element-559">
                        Sustained Effect:
                      </span>{' '}
                      +14% post-flight (directional)
                    </span>
                  </li>
                  <li className="flex items-start space-x-2" data-id="element-560">
                    <CheckCircleIcon className="h-4 w-4 text-[#AAAAAA] mt-0.5 flex-shrink-0" data-id="element-561" />
                    <span className="text-sm text-[#000000]" data-id="element-562">
                      <span className="font-bold text-[#171717]" data-id="element-563">Quality:</span>{' '}
                      GDPR compliant, premium publishers
                    </span>
                  </li>
                </ul>
              </div>

              {/* Common Questions */}
              <div data-id="element-564">
                <h3 className="text-lg font-bold text-[#171717] mb-4 flex items-center" data-id="element-565">
                  <HelpCircleIcon className="h-5 w-5 text-[#AAAAAA] mr-2" data-id="element-566" />
                  Common Questions
                </h3>
                <div className="space-y-4" data-id="element-567">
                  <div className="bg-[#F9FAFB] p-4 rounded-sm border border-[#E5E5E5]" data-id="element-568">
                    <h4 className="text-sm font-bold text-[#171717] mb-2" data-id="element-569">
                      How is this different from traditional brand lift studies?
                    </h4>
                    <p className="text-sm text-[#000000] leading-relaxed" data-id="element-570">
                      Traditional studies survey a small sample about what they
                      remember. Brandpulse observes the entire target audience's
                      actual behavior in real-time, measured in their natural
                      viewing environment. We measure what people do, not what
                      they claim to recall.
                    </p>
                  </div>
                  <div className="bg-[#F9FAFB] p-4 rounded-sm border border-[#E5E5E5]" data-id="element-571">
                    <h4 className="text-sm font-bold text-[#171717] mb-2" data-id="element-572">
                      Can we trust behavioral data over surveys?
                    </h4>
                    <p className="text-sm text-[#000000] leading-relaxed" data-id="element-573">
                      Behavior reveals truth, surveys reveal memory. When
                      someone actively engages with your brand, that's a
                      stronger predictor of purchase intent than claimed
                      awareness weeks after a campaign. Actions speak louder
                      than words.
                    </p>
                  </div>
                  <div className="bg-[#F9FAFB] p-4 rounded-sm border border-[#E5E5E5]" data-id="element-574">
                    <h4 className="text-sm font-bold text-[#171717] mb-2" data-id="element-575">
                      What if external factors influenced the results?
                    </h4>
                    <p className="text-sm text-[#000000] leading-relaxed mb-3" data-id="element-576">
                      Our three-phase design with audience exclusion isolates
                      your campaign's impact. By measuring the same target
                      demographic before, during, and after your TV flight,
                      while keeping the measurement stimulus constant, we
                      attribute behavioral changes to your media pressure.
                    </p>
                    <p className="text-sm text-[#000000] leading-relaxed" data-id="element-577">
                      <span className="font-bold text-[#171717]" data-id="element-578">
                        Important context:
                      </span>{' '}
                      This is a real-world measurement, not a controlled lab
                      experiment. External factors (news events, competitor
                      campaigns, market conditions) are part of the reality we
                      measure. If your campaign shows no lift despite strong
                      creative, that's valuable intelligence, it tells you
                      external headwinds prevented breakthrough. Conversely,
                      strong lift despite external noise proves your campaign's
                      power.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};